/**
 * Comprehensive Schema.org Structured Data for SEO
 * Implements Organization, LocalBusiness, Services, Team, Projects, Articles, and Breadcrumbs
 */

const SITE_URL = 'https://www.furd.co.uk';
const COMPANY_NAME = 'FURD Ltd';
const COMPANY_LOGO = 'https://customer-assets.emergentagent.com/job_furd-mep/artifacts/93z01v59_FURD.jpg';

// Organization Schema - Core company information
export const generateOrganizationSchema = (companyInfo = {}) => {
  return {
    "@context": "https://schema.org",
    "@type": ["Organization", "ProfessionalService"],
    "@id": `${SITE_URL}/#organization`,
    "name": companyInfo.name || COMPANY_NAME,
    "alternateName": "FURD Engineering & Design Consultancy",
    "url": SITE_URL,
    "logo": {
      "@type": "ImageObject",
      "url": companyInfo.logo || COMPANY_LOGO,
      "width": 512,
      "height": 512
    },
    "image": companyInfo.logo || COMPANY_LOGO,
    "description": companyInfo.description || "Leading UK MEP Engineering & Building Services Consultancy. Expert mechanical, electrical, and public health design for commercial, residential, healthcare, and education projects.",
    "foundingDate": "2021",
    "founder": {
      "@type": "Person",
      "name": "FURD Founders"
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": companyInfo.address || "Office 7, 35-37 Ludgate Hill",
      "addressLocality": "London",
      "addressRegion": "Greater London",
      "postalCode": companyInfo.postcode || "EC4M 7JN",
      "addressCountry": "GB"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 51.513458,
      "longitude": -0.098946
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": companyInfo.phone || "+44-20-7183-5251",
        "contactType": "sales",
        "email": companyInfo.email || "info@furd.co.uk",
        "areaServed": ["GB", "United Kingdom"],
        "availableLanguage": ["English"]
      },
      {
        "@type": "ContactPoint",
        "telephone": companyInfo.phone || "+44-20-7183-5251",
        "contactType": "customer service",
        "email": companyInfo.email || "info@furd.co.uk",
        "areaServed": ["GB", "United Kingdom"],
        "availableLanguage": ["English"]
      }
    ],
    "sameAs": [
      companyInfo.linkedin || "https://www.linkedin.com/company/furd-ltd"
    ],
    "knowsAbout": [
      "MEP Engineering",
      "Building Services Engineering",
      "Mechanical Design",
      "Electrical Design", 
      "Public Health Design",
      "HVAC Design",
      "Sustainability Consultancy",
      "Net Zero Carbon Design",
      "Building Physics",
      "Part L Compliance",
      "Part O Compliance",
      "Decarbonisation",
      "Heat Pump Systems",
      "Embodied Carbon Assessment"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "MEP Engineering Services",
      "itemListElement": [
        {
          "@type": "OfferCatalog",
          "name": "Mechanical Engineering",
          "itemListElement": [
            {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "HVAC Design"}},
            {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Heating Systems Design"}},
            {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Ventilation Design"}}
          ]
        },
        {
          "@type": "OfferCatalog", 
          "name": "Electrical Engineering",
          "itemListElement": [
            {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Electrical Distribution Design"}},
            {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Lighting Design"}},
            {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Fire Alarm Systems"}}
          ]
        },
        {
          "@type": "OfferCatalog",
          "name": "Sustainability Services", 
          "itemListElement": [
            {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Net Zero Design"}},
            {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "Part L Compliance"}},
            {"@type": "Offer", "itemOffered": {"@type": "Service", "name": "BREEAM Assessment"}}
          ]
        }
      ]
    },
    "areaServed": [
      {
        "@type": "Country",
        "name": "United Kingdom"
      },
      {
        "@type": "City",
        "name": "London"
      },
      {
        "@type": "AdministrativeArea",
        "name": "South East England"
      }
    ],
    "slogan": "Engineering Excellence for the Built Environment"
  };
};

// LocalBusiness Schema - For local SEO
export const generateLocalBusinessSchema = (companyInfo = {}) => {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${SITE_URL}/#localbusiness`,
    "name": companyInfo.name || COMPANY_NAME,
    "image": companyInfo.logo || COMPANY_LOGO,
    "url": SITE_URL,
    "telephone": companyInfo.phone || "+44-20-7183-5251",
    "email": companyInfo.email || "info@furd.co.uk",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": companyInfo.address || "Office 7, 35-37 Ludgate Hill",
      "addressLocality": "London",
      "addressRegion": "Greater London",
      "postalCode": companyInfo.postcode || "EC4M 7JN",
      "addressCountry": "GB"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 51.513458,
      "longitude": -0.098946
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      }
    ],
    "priceRange": "££££",
    "currenciesAccepted": "GBP",
    "paymentAccepted": "Bank Transfer, Credit Card"
  };
};

// Service Schema - For individual services
export const generateServiceSchema = (service, index = 0) => {
  const serviceUrl = `${SITE_URL}/services/${service.slug || service.id}`;
  
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${serviceUrl}/#service`,
    "name": service.title || service.name,
    "serviceType": service.title || service.name,
    "description": service.seoDescription || service.description,
    "url": serviceUrl,
    "image": service.image,
    "provider": {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": COMPANY_NAME
    },
    "areaServed": [
      {"@type": "Country", "name": "United Kingdom"},
      {"@type": "City", "name": "London"}
    ],
    "hasOfferCatalog": service.features ? {
      "@type": "OfferCatalog",
      "name": `${service.title} Features`,
      "itemListElement": service.features.map((feature, i) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": feature
        }
      }))
    } : undefined,
    "category": "Building Services Engineering",
    "termsOfService": `${SITE_URL}/contact`,
    "audience": {
      "@type": "Audience",
      "audienceType": "Developers, Architects, Contractors, Local Authorities"
    }
  };
};

// Services List Schema
export const generateServicesListSchema = (services) => {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "MEP Engineering & Building Services",
    "description": "Comprehensive MEP engineering and building services consultancy in the UK",
    "numberOfItems": services.length,
    "itemListElement": services.map((service, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": generateServiceSchema(service, index)
    }))
  };
};

// Team Member / Person Schema
export const generatePersonSchema = (member) => {
  const memberUrl = `${SITE_URL}/team/${member.slug || member.id}`;
  
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${memberUrl}/#person`,
    "name": member.name,
    "jobTitle": member.role || member.position,
    "description": member.bio || member.shortBio,
    "image": member.image,
    "url": memberUrl,
    "worksFor": {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": COMPANY_NAME
    },
    "knowsAbout": member.expertise || member.specializations || [],
    "alumniOf": member.qualifications ? member.qualifications.map(q => ({
      "@type": "EducationalOrganization",
      "name": q
    })) : undefined,
    "award": member.accreditations || [],
    "sameAs": member.linkedin ? [member.linkedin] : []
  };
};

// Team List Schema
export const generateTeamSchema = (members) => {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "FURD Engineering Team",
    "description": "Meet our team of expert MEP engineers and building services consultants",
    "numberOfItems": members.length,
    "itemListElement": members.map((member, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": generatePersonSchema(member)
    }))
  };
};

// Project Schema (using CreativeWork for engineering projects)
export const generateProjectSchema = (project) => {
  const projectUrl = `${SITE_URL}/projects/${project.slug || project.id}`;
  
  return {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "@id": `${projectUrl}/#project`,
    "name": project.name || project.title,
    "description": project.seoDescription || project.description,
    "url": projectUrl,
    "image": project.image || (project.images && project.images[0]),
    "dateCreated": project.completionDate || project.createdAt,
    "creator": {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": COMPANY_NAME
    },
    "locationCreated": project.location ? {
      "@type": "Place",
      "name": project.location
    } : undefined,
    "about": {
      "@type": "Thing",
      "name": project.sector || project.category || "Building Services Engineering"
    },
    "keywords": project.seoKeywords || [project.sector, "MEP", "Building Services"].filter(Boolean).join(", "),
    "client": project.client ? {
      "@type": "Organization",
      "name": project.client
    } : undefined
  };
};

// Projects List Schema
export const generateProjectsListSchema = (projects) => {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "FURD Engineering Projects Portfolio",
    "description": "MEP engineering and building services projects across the UK",
    "numberOfItems": projects.length,
    "itemListElement": projects.map((project, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": generateProjectSchema(project)
    }))
  };
};

// Article Schema - For News/Insights/Stay Informed
export const generateArticleSchema = (article) => {
  const articleUrl = `${SITE_URL}/news/${article.slug || article.id}`;
  
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${articleUrl}/#article`,
    "headline": article.title,
    "name": article.title,
    "description": article.seoDescription || article.excerpt || article.summary,
    "articleBody": article.content,
    "url": articleUrl,
    "image": article.image || article.featuredImage,
    "datePublished": article.publishedAt || article.createdAt,
    "dateModified": article.updatedAt || article.publishedAt,
    "author": {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": COMPANY_NAME
    },
    "publisher": {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": COMPANY_NAME,
      "logo": {
        "@type": "ImageObject",
        "url": COMPANY_LOGO
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": articleUrl
    },
    "keywords": article.seoKeywords || article.tags?.join(", "),
    "articleSection": article.category || "Industry Insights",
    "inLanguage": "en-GB"
  };
};

// Breadcrumb Schema
export const generateBreadcrumbSchema = (items) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`
    }))
  };
};

// WebPage Schema
export const generateWebPageSchema = (page) => {
  const pageUrl = page.url || SITE_URL;
  
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${pageUrl}/#webpage`,
    "url": pageUrl,
    "name": page.title,
    "description": page.description,
    "isPartOf": {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      "name": COMPANY_NAME,
      "url": SITE_URL
    },
    "about": {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`
    },
    "inLanguage": "en-GB",
    "datePublished": page.publishedAt,
    "dateModified": page.updatedAt
  };
};

// WebSite Schema
export const generateWebSiteSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    "url": SITE_URL,
    "name": COMPANY_NAME,
    "description": "Leading UK MEP Engineering & Building Services Consultancy",
    "publisher": {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`
    },
    "inLanguage": "en-GB"
  };
};

// Sector/Industry Schema
export const generateSectorSchema = (sector) => {
  const sectorUrl = `${SITE_URL}/sectors/${sector.slug || sector.id}`;
  
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${sectorUrl}/#sector`,
    "name": `${sector.name} MEP Engineering`,
    "serviceType": `${sector.name} Building Services`,
    "description": sector.seoDescription || sector.description,
    "url": sectorUrl,
    "image": sector.image,
    "provider": {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      "name": COMPANY_NAME
    },
    "areaServed": {
      "@type": "Country",
      "name": "United Kingdom"
    },
    "audience": {
      "@type": "Audience",
      "audienceType": sector.targetAudience || "Property Developers, Architects, Contractors"
    }
  };
};

// FAQ Schema - For FAQ sections
export const generateFAQSchema = (faqs) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

// Component to render structured data
export const StructuredData = ({ data }) => {
  if (!data) return null;
  
  // Handle array of schemas
  const schemas = Array.isArray(data) ? data : [data];
  
  return (
    <>
      {schemas.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </>
  );
};

// Combined schema for homepage
export const generateHomePageSchemas = (companyInfo = {}) => {
  return [
    generateWebSiteSchema(),
    generateOrganizationSchema(companyInfo),
    generateLocalBusinessSchema(companyInfo)
  ];
};

export default StructuredData;
